import React from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { UserType } from './utils';
import { FeedbackStatus } from '../api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faBadgeCheck,
  faChartLineUp,
  faFileMagnifyingGlass,
} from '@fortawesome/pro-light-svg-icons';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { TrueImpactButtonWithLoading } from 'shared/design-system/components/TrueImpactButton';
import { isCnUserView } from 'shared/utils/partnerUser';

interface FeedbackAwaitingProps {
  userType: UserType;
  feedbackStatus: FeedbackStatus;
  className?: string;
}

export const getFinishedFeedbackButton = (type?: UserType) => {
  const pathname = window.location.pathname;

  // Check if user is a CN (Charity Navigator) user
  const isCNUser = isCnUserView();

  if (pathname.startsWith('/donor')) {
    return {
      text: 'Feedback dashboard',
      link: '/donor/feedback',
    };
  }
  if (pathname.startsWith('/npo')) {
    // Hide "View to-do tasks" button for CN users
    if (isCNUser || type === 'cn-npo') {
      return undefined;
    }
    return {
      text: 'View to-do tasks',
      link: '/npo/todo/by-due-date',
    };
  }
  if (pathname.startsWith('/account')) {
    return {
      text: 'Feedback dashboard',
      link: '/account/feedback',
    };
  }
  return undefined;
};

export const getFinishedReportText = (
  status: FeedbackStatus,
  type: UserType,
) => {
  switch (status) {
    case 'Draft':
    case 'Interim Updates Needed':
    case 'Final Updates Needed':
      return {
        heading: 'The report is not ready for feedback or review',
        subHeadingOne:
          'Until the report is submitted for feedback, you will not be able to review the report. Please complete all the sections ' +
            'of the report builder and submit the report for feedback.',
        subHeadingTwo: undefined,
        icon: (
          <FontAwesomeIcon
            icon={faEnvelope}
            className="icon"
            color={COLORS.ctaPrimary}
          />
        ),
      };
    case 'Awaiting TI Feedback':
    case 'Awaiting Funder Feedback':
    case 'Awaiting TI Verification':
    case 'Awaiting Funder Verification':
      return {
        heading: 'The report is under review',
        subHeadingOne:
          'The report is under review. Once we collect all the feedback we will notify you.',
        subHeadingTwo: undefined,
        icon: (
          <FontAwesomeIcon
            icon={faFileMagnifyingGlass}
            className="icon"
            color={COLORS.ctaPrimary}
          />
        ),
      };
    case 'TI Feedback Provided':
    case 'Funder Feedback Provided':
      return {
        heading: 'The report is under improvement',
        subHeadingOne:
          'We collected all your feedback for this report. We are giving this moment to the organization to update their answers or ' +
            'clarify your questions. Either way this report on the right track.',
        subHeadingTwo: undefined,
        icon: (
          <FontAwesomeIcon
            icon={faChartLineUp}
            className="icon"
            color={COLORS.ctaPrimary}
          />
        ),
      };
    case 'Published':
    case 'Finalized':
    default: {
      const statusText = status.toLowerCase();
      return {
        heading: `The report is now ${statusText}`,
        subHeadingOne: `The report is now ${statusText}. This means that we are no longer accepting new feedback for this report.`,
        subHeadingTwo: undefined,
        icon: (
          <FontAwesomeIcon
            icon={faBadgeCheck}
            className="icon"
            color={COLORS.success}
          />
        ),
      };
    }
  }
};

const StyledDiv = styled.div`
  height: 100%;
  background-color: ${COLORS.whiteBase};
  padding: 76px;
  text-align: center;
  .icon {
    width: 40px;
    height: 40px;
  }
  .heading {
    margin-top: ${SPACERS.medium};
    margin-bottom: ${SPACERS.small};
  }
  .subheading {
    margin-bottom: ${SPACERS.small};
  }
  .button {
    margin-top: ${SPACERS.extraLarge};
  }
`;

const FeedbackAwaiting: React.FC<FeedbackAwaitingProps> = ({
  feedbackStatus,
  userType,
  className,
}) => {
  const { push } = useHistory();
  const button = getFinishedFeedbackButton(userType);
  const text = getFinishedReportText(feedbackStatus, userType);
  return (
    <StyledDiv className={className}>
      {text.icon}
      <TrueImpactText variant="heading" className="heading">
        {text.heading}
      </TrueImpactText>
      {text.subHeadingOne && (
        <TrueImpactText className="subheading">
          {text.subHeadingOne}
        </TrueImpactText>
      )}
      {text.subHeadingTwo && (
        <TrueImpactText>{text.subHeadingTwo}</TrueImpactText>
      )}
      {button && (
        <TrueImpactButtonWithLoading
          className="button"
          onClick={() => push(button.link)}
        >
          {button.text}
        </TrueImpactButtonWithLoading>
      )}
    </StyledDiv>
  );
};

export default FeedbackAwaiting;
